/**
 * Payment Service (Frontend)
 * Centralized payment handling that mirrors backend PaymentService.
 * Supports all payment types: plugins, deposits, withdrawals, subscriptions.
 */

import api from './api';

// Payment types (matching backend)
export const PAYMENT_TYPES = {
  PLUGIN_PURCHASE: 'plugin_purchase',
  DEPOSIT: 'deposit',
  WITHDRAWAL: 'withdrawal',
  SUBSCRIPTION: 'subscription',
  CREDIT_TOPUP: 'credit_topup',
  LICENSE_RENEWAL: 'license_renewal',
};

// Payment status
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  EXPIRED: 'expired',
  FAILED: 'failed',
  REFUNDED: 'refunded',
};

// Gateway types
export const GATEWAY_TYPES = {
  AMORPAY: 'amorpay',
  PAYSTACK: 'paystack',
};

class PaymentService {
  
  /**
   * Get payment options for a gateway
   * @param {string} gateway - Gateway name (amorpay, paystack)
   * @returns {Promise<Array>} Payment options with networks and currencies
   */
  async getPaymentOptions(gateway = 'amorpay') {
    try {
      const response = await api.get('/broker/plugins/payment-options', {
        params: { gateway }
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to get payment options:', error);
      throw error;
    }
  }

  /**
   * Create a payment (universal method)
   * @param {Object} params
   * @param {string} params.type - Payment type (plugin_purchase, deposit, etc.)
   * @param {string} params.gateway - Gateway (amorpay, paystack)
   * @param {string} params.currency - Currency (ETH, USDT, BTC, etc.)
   * @param {string} params.network - Network (ethereum, bsc, bitcoin, etc.)
   * @param {number} params.amount - Amount (optional for plugins, amount comes from plugin price)
   * @param {Object} params.metadata - Additional data (plugin_key, account_id, etc.)
   * @returns {Promise<Object>} Payment details
   */
  async createPayment({ type, gateway = 'amorpay', currency, network, amount, metadata = {} }) {
    try {
      // Route to appropriate endpoint based on type
      switch (type) {
        case PAYMENT_TYPES.PLUGIN_PURCHASE:
          return await this.createPluginPayment(metadata.plugin_key, currency, network);
          
        case PAYMENT_TYPES.DEPOSIT:
          return await this.createDeposit(metadata.account_id, amount, currency, network);
          
        case PAYMENT_TYPES.SUBSCRIPTION:
          return await this.createSubscription(metadata.plan_id, currency, network);
          
        default:
          throw new Error(`Payment type ${type} not yet implemented in frontend`);
      }
    } catch (error) {
      console.error('Failed to create payment:', error);
      throw error;
    }
  }

  /**
   * Create plugin purchase payment
   * @param {string} pluginKey - Plugin identifier
   * @param {string} currency - Currency (ETH, USDT, BTC, etc.)
   * @param {string} network - Network (ethereum, bsc, bitcoin, etc.)
   * @returns {Promise<Object>} Payment details
   */
  async createPluginPayment(pluginKey, currency, network) {
    try {
      const response = await api.post(`/broker/plugins/${pluginKey}/purchase`, {
        currency,
        network,
      });
      
      const data = response.data.data || response.data;
      
      // Normalize response to match payment structure
      return {
        payment_id: data.paymentId || data.payment_id,
        gateway_payment_id: data.paymentId || data.payment_id,
        type: PAYMENT_TYPES.PLUGIN_PURCHASE,
        amount: data.amount,
        currency: data.currency,
        network: data.network,
        payment_address: data.walletAddress || data.payment_address,
        status: PAYMENT_STATUS.PENDING,
        expires_at: data.expiresAt || data.expires_at,
        metadata: {
          plugin_key: pluginKey,
          plugin_name: data.pluginName,
        },
        // Include original response for backward compatibility
        ...data,
      };
    } catch (error) {
      console.error('Failed to create plugin payment:', error);
      throw error;
    }
  }

  /**
   * Create deposit payment
   * @param {number} accountId - Trading account ID
   * @param {number} amount - Amount to deposit
   * @param {string} currency - Currency (USDT, ETH, etc.)
   * @param {string} network - Network (ethereum, bsc, etc.)
   * @returns {Promise<Object>} Payment details
   */
  async createDeposit(accountId, amount, currency, network) {
    try {
      const response = await api.post('/broker/payments/deposits/create', {
        account_id: accountId,
        amount,
        currency,
        network,
      });
      
      return this._normalizePaymentResponse(response.data, PAYMENT_TYPES.DEPOSIT);
    } catch (error) {
      console.error('Failed to create deposit payment:', error);
      throw error;
    }
  }

  /**
   * Create subscription payment
   * @param {string} planId - Subscription plan ID
   * @param {string} currency - Currency
   * @param {string} network - Network
   * @returns {Promise<Object>} Payment details
   */
  async createSubscription(planId, currency, network) {
    try {
      const response = await api.post('/broker/subscriptions/purchase', {
        plan_id: planId,
        currency,
        network,
      });
      
      return this._normalizePaymentResponse(response.data, PAYMENT_TYPES.SUBSCRIPTION);
    } catch (error) {
      console.error('Failed to create subscription payment:', error);
      throw error;
    }
  }

  /**
   * Get payment status
   * @param {string} paymentId - Payment ID or gateway payment ID
   * @param {string} type - Payment type (optional, helps route to correct endpoint)
   * @returns {Promise<Object>} Payment status
   */
  async getPaymentStatus(paymentId, type = null) {
    try {
      let response;
      
      // Route to appropriate endpoint based on type
      if (type === PAYMENT_TYPES.PLUGIN_PURCHASE || !type) {
        // Default to plugin payment endpoint for backward compatibility
        response = await api.get(`/broker/plugins/payments/${paymentId}/status`);
      } else {
        // Generic payment status endpoint (to be implemented)
        response = await api.get(`/broker/payments/${paymentId}/status`);
      }
      
      const data = response.data.data || response.data;
      
      return {
        payment_id: data.paymentId || data.payment_id,
        status: data.status,
        type: data.type || type,
        amount: data.amount,
        currency: data.currency,
        network: data.network,
        payment_address: data.walletAddress || data.payment_address,
        tx_hash: data.txHash || data.tx_hash,
        confirmations: data.confirmations || 0,
        confirmed: data.activated || data.status === 'confirmed',
        created_at: data.createdAt || data.created_at,
        expires_at: data.expiresAt || data.expires_at,
        metadata: data.metadata || {},
      };
    } catch (error) {
      console.error('Failed to get payment status:', error);
      throw error;
    }
  }

  /**
   * Get payment history
   * @param {Object} filters - Filter options
   * @param {string} filters.type - Payment type
   * @param {string} filters.status - Payment status
   * @param {number} filters.limit - Number of results
   * @returns {Promise<Array>} Payment history
   */
  async getPaymentHistory(filters = {}) {
    try {
      // For now, route to plugin payments (expand later for other types)
      const response = await api.get('/broker/plugins/payments', {
        params: filters
      });
      
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to get payment history:', error);
      throw error;
    }
  }

  /**
   * Poll payment status with exponential backoff
   * @param {string} paymentId - Payment ID
   * @param {string} type - Payment type
   * @param {Function} onUpdate - Callback when status changes
   * @param {Object} options - Polling options
   * @returns {Object} { stop: Function } - Controller to stop polling
   */
  pollPaymentStatus(paymentId, type, onUpdate, options = {}) {
    const {
      initialInterval = 5000,    // Start with 5 seconds
      maxInterval = 30000,        // Max 30 seconds
      maxAttempts = 60,           // Stop after 60 attempts (5 minutes with backoff)
    } = options;

    let attempts = 0;
    let interval = initialInterval;
    let timeoutId = null;
    let stopped = false;

    const poll = async () => {
      if (stopped || attempts >= maxAttempts) {
        console.log('Polling stopped', { attempts, maxAttempts, stopped });
        return;
      }

      attempts++;

      try {
        const status = await this.getPaymentStatus(paymentId, type);
        
        // Call update callback
        if (onUpdate) {
          onUpdate(status);
        }

        // Stop if payment is final
        if (status.status === 'confirmed' || 
            status.status === 'expired' || 
            status.status === 'failed' ||
            status.confirmed) {
          console.log('Payment reached final status:', status.status);
          return;
        }

        // Exponential backoff
        interval = Math.min(interval * 1.2, maxInterval);
        
      } catch (error) {
        console.warn('Payment status poll failed:', error.message);
        // Continue polling on error
      }

      // Schedule next poll
      if (!stopped && attempts < maxAttempts) {
        timeoutId = setTimeout(poll, interval);
      }
    };

    // Start polling
    poll();

    // Return controller
    return {
      stop: () => {
        stopped = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    };
  }

  /**
   * Normalize payment response to consistent format
   * @private
   */
  _normalizePaymentResponse(data, type) {
    const normalized = data.data || data;
    
    return {
      payment_id: normalized.payment_id || normalized.paymentId,
      gateway_payment_id: normalized.gateway_payment_id || normalized.paymentId,
      type,
      amount: normalized.amount,
      currency: normalized.currency,
      network: normalized.network,
      payment_address: normalized.payment_address || normalized.walletAddress,
      status: normalized.status || PAYMENT_STATUS.PENDING,
      expires_at: normalized.expires_at || normalized.expiresAt,
      metadata: normalized.metadata || {},
      ...normalized,
    };
  }
}

// Export singleton instance
export default new PaymentService();

// Also export class for testing
export { PaymentService };